import { defineStore } from '@/store/define-store';
import { hrGetTenantInfo } from '@/modules/people/facades/hr-get-tenant-info';
import getTenantSettings from '@/lib/request/svc/hr/hr-tenant-settings-get';
import { createTenantSettings } from '@/lib/request/svc/hr/hr-tenant-settings-post';
import getInstallationId from '@/lib/facades/get-installation-id';
import { HR_ONBOARDING_STATES } from '@/lib/request/svc/hr';
import getVueEventBus from '@/lib/vue-event-bus';
import PEOPLE_EVENTS from '@/modules/people/events';

/**
 * @typedef HrDocumentFieldType
 * @property {'UNDEFINED', 'STRING', 'EMAIL', 'PASSWORD', 'PASSWORD_SIMPLE',
 *  'PASSWORD_COMPLEX', 'NUMBER', 'E164_PHONE', 'LINK', 'LINK_BUTTON', 'SELECT',
 *  'RADIO', 'CHECK', 'FILE_UPLOAD', 'OPTION', 'REPEATED', 'CONTENT', 'DOCSIGNATURE_TEMPLATE',
 *  'DOCSIGNATURE_REQUEST', 'DATE_AU', 'DATE_US', 'DATE_ISO8601', 'ADDRESS_AU', 'BSB_AU',
 *  'TFN_AU', 'SUPERFUND_AU'}
 */

/**
 * @typedef DocumentSignatureEmbedValue
 * @property {String} provider
 * @property {{clientID: String, title: String, templateID: String, sandbox: String}} providerData
 */

/**
 * @typedef SuperfundAUValue
 * @property {String} ABN
 * @property {String} USI
 * @property {String} ProductName
 */

/**
 * @typedef FileValue
 * @property {String} id
 * @property {String} name
 * @property {String} date
 * @property {String} content
 * @property {String} sizeInBytes
 * @property {String} type
 */
/**
 * @typedef HrOnboardingFieldValue
 * @property {String} name
 * @property {String} description
 * @property {HrDocumentFieldType} type
 * @property {DocumentSignatureEmbedValue=} docSignatureEmbedValue
 * @property {SuperfundAUValue=} superfundAUValue
 * @property {FileValue=} fileValue
 */

/**
 * @typedef HrDocsignatureTemplate
 * @property {String} templateId
 * @property {String} description
 * @property {String} title
 * @property {String} file
 * @property {{fullName: String, email: String, order: Integer}[]} signer
 * @property {Object[]} customFields
 */

/**
 * @typedef HrDocsignatureTemplatesToSubmit
 * @property {String} name
 * @property {HrOnboardingFieldValue[]} fields
 */

/**
 * @typedef HrTenantInfo
 * @property {String} tenantId
 * @property {String} tenantName
 * @property {String} tenantUrl
 */

/**
 * @typedef HrState
 * @property {boolean} isEnabled
 * @property {HrDocsignatureTemplatesToSubmit} hrDocsignatureTemplatesToSubmit
 * @property {HrDocsignatureTemplate[]} hrDocsignatureTemplates
 * @property {HrTenantInfo} hrTenantInfo
 * @property {Questions[]} questions
 * @property {HrDocumentsToUpload[]} hrDocumentsToUpload
 * @property {HrDocumentsToSign[]} hrDocumentsToSign
 * @property {EmployeesToExport[]} employeesToExport
 * @property {OnboardingDataToExport[]} onboardingDataToExport
 */
const useHrStore = defineStore('webapp:people:hr', {
  state: () => {
    return {
      isEnabled: false,
      isDocumentManagementEnabled: false,
      isOngoingRTWEnabled: false,
      isTeamPerformanceEnabled: false,
      isTeamPerformanceListenerInitialized: false,
      employeeIndexedOnboardingIds: {},
      employeeIndexedOnboarding: {},
      hrSettings: {},
      hrSettingsDocumentsIndexed: {},
      hrSettingsDocumentsFieldsIndexed: {},
      hrDocsignatureSettings: {},
      hrTenantInfo: {},
      hrDocsignatureTemplates: [],
      hrDocsignatureTemplatesToSubmit: {
        name: 'docSignatureRequests',
        fields: [],
      },
      templateToEdit: [],
      hrDocumentsToUpload: [],
      hrDocumentsToSign: [],
      status: {
        isLoadingOnboarding: false,
        isRequestingChanges: false,
      },
      errors: {
        onboardingLoadError: null,
        requestChangesError: null,
      },
      onboardingDefaults: [],
      onboardingFileUploadFields: [],
      onboardingSuperfund: {
        company: {},
      },
      templatesToCreate: {},
      remainingOnboarding: null,
      questions: [],
      employeesToExport: [],
      onboardingDataToExport: [],
    };
  },
  getters: {
    getIsLoading(state) {
      return state.status.isLoadingOnboarding;
    },
  },
  actions: {
    /**
     * @param {Boolean} isEnabled
     */
    setIsEnabled(isEnabled = false) {
      this.isEnabled = isEnabled;
    },
    /**
     * @param {Boolean} isEnabled
     */
    setIsDocumentManagementEnabled(isEnabled = false) {
      this.isDocumentManagementEnabled = isEnabled;
    },
    /**
     * @param {Boolean} isEnabled
     */
    setIsOngoingRTWEnabled(isEnabled = false) {
      this.isOngoingRTWEnabled = isEnabled;
    },
    /**
     * @param {Boolean} isEnabled
     */
    setIsTeamPerformanceEnabled(isEnabled = false) {
      this.isTeamPerformanceEnabled = isEnabled;
    },
    /**
     * @param {Boolean} initialValue - from session.getSetup().IsTeamPerformanceEnabled
     */
    initializeTeamPerformanceListener(initialValue = false) {
      if (this.isTeamPerformanceListenerInitialized) {
        return;
      }

      this.isTeamPerformanceEnabled = initialValue;

      const eventBus = getVueEventBus();
      eventBus.$on(
        PEOPLE_EVENTS.TEAM_PERFORMANCE_SETTINGS_CHANGED,
        ({ enabled } = {}) => {
          this.setIsTeamPerformanceEnabled(enabled);
        }
      );

      this.isTeamPerformanceListenerInitialized = true;
    },
    /**
     * @param {HrState=} state
     * @return {HrTenantInfo}
     */
    getTenantInfo() {
      if (!this.hrTenantInfo.tenantId) {
        this.hrTenantInfo = hrGetTenantInfo();
      }

      return this.hrTenantInfo;
    },
    getDocumentSettingsFieldsByDocumentName(documentName) {
      return this.hrSettingsDocumentsIndexed?.[documentName]?.fields || [];
    },
    /**
     * @param {Number} employeeId
     * @return {String|undefined} onboardingId
     */
    getOnboardingIdByEmployeeId(employeeId) {
      return this.employeeIndexedOnboardingIds[employeeId] || undefined;
    },
    /**
     * @param {Number} employeeId
     * @param {String} onboardingId
     */
    setOnboardingIdByEmployeeId(employeeId, onboardingId) {
      this.employeeIndexedOnboardingIds[employeeId] = onboardingId;
    },
    /**
     * @param {Number} employeeId
     * @return {String|undefined} onboardingId
     */
    getOnboardingByEmployeeId(employeeId) {
      return this.employeeIndexedOnboarding[employeeId] || {};
    },
    /**
     * @param {Number|String} employeeId
     * @param {Object} onboarding // Onboarding Object
     */
    setOnboardingByEmployeeId(employeeId, onboarding) {
      this.employeeIndexedOnboarding[employeeId] = onboarding;
    },
    /**
     * @param {Number|String} employeeId
     * @param {String} state // Onboarding State
     */
    setOnboardingStateByEmployeeId(employeeId, state) {
      this.employeeIndexedOnboarding[employeeId].state = state;
    },
    /**
     * @param {Object[]} docsignatureTemplates
     *  @param {String} docsignatureTemplates[].id
     */
    setDocsignatureTemplates(docsignatureTemplates = []) {
      this.hrDocsignatureTemplates = docsignatureTemplates;
    },
    /**
     * @param {Object[]} docsignatureSettings
     *  @param {String} docsignatureSettings[].id
     */
    setDocsignatureSettings(docsignatureSettings = []) {
      this.hrDocsignatureSettings = docsignatureSettings;
    },
    /**
     * @param {Object[]} documents
     *  @param {String} documents[].documentName
     *  @param {Object[]} documents[].fields
     */
    setSettingsDocuments(documents = []) {
      this.hrSettings.documents = documents;
    },
    /**
     * @param {String} id
     */
    setSettingsId(id = '') {
      this.hrSettings.id = id;
    },
    /**
     * @param {Object} info
     *  @param {String} info.tenantId
     *  @param {String} info.tenantName
     *  @param {String} info.tenantUrl
     */
    setHRTenantInfo(info = {}) {
      this.hrTenantInfo = info;
    },
    /**
     * @param {Object} settings
     */
    setSettings(settings = {}) {
      this.hrSettings = settings;
      const hrSettingsDocumentsFieldsIndexed = {};

      // index documents
      this.setSettingsDocumentsIndexed(
        this.hrSettings?.documents?.reduce((reduced, doc) => {
          // index fields from document
          doc.fields?.forEach(
            field => (hrSettingsDocumentsFieldsIndexed[field.name] = field)
          );

          reduced[doc.documentName] = doc;
          return reduced;
        }, {}) || {}
      );

      this.setSettingsDocumentsFieldsIndexed(hrSettingsDocumentsFieldsIndexed);
    },
    async fetchSettings() {
      const tenantId = getInstallationId();
      let tenantSetting = await getTenantSettings(tenantId);

      if (!Object.keys(tenantSetting).length) {
        tenantSetting = await createTenantSettings(tenantId);
      }

      this.setSettings(tenantSetting);
    },
    getSettings() {
      return this.hrSettings || {};
    },
    /**
     * @param {Object} documents
     */
    setSettingsDocumentsIndexed(documents = {}) {
      this.hrSettingsDocumentsIndexed = documents;
    },
    /**
     * @param {Object} fields
     */
    setSettingsDocumentsFieldsIndexed(fields = {}) {
      this.hrSettingsDocumentsFieldsIndexed = fields;
    },
    /**
     * @param {Object} remainingOnboarding
     */
    setRemainingOnboarding(remainingOnboarding = {}) {
      this.remainingOnboarding = remainingOnboarding;
    },
    getEmployeesToExport() {
      return this.employeesToExport;
    },
    getOnboardingDataToExport() {
      return this.onboardingDataToExport;
    },
    setIsLoading(isLoading = true) {
      this.status.isLoadingOnboarding = isLoading;
    },
    setQuestions(questions = []) {
      this.questions = questions;
    },
    setDocumentsToUpload(documents = []) {
      this.hrDocumentsToUpload = documents;
    },
    getActiveStepByEmployeeId(employeeId = 0) {
      return (
        this.employeeIndexedOnboarding[employeeId]?.state ||
        HR_ONBOARDING_STATES.TODO
      );
    },
    /**
     * @param {String} name
     * @param {any} value
     */
    setError(name, value) {
      this.errors[name] = value;
    },
  },
});

export default useHrStore;
